import { useMemo } from 'react';

/**
 * Builds a map of per-class hectare equivalent keys for metadata flattening.
 * Keys follow the pattern 'hectare_equivalent:<className>' so that
 * getPropertyValue's default case resolves them via meta[propertyId].
 *
 * @param {Array} detectionClasses - Array of { className, hectare_equivalent, ... }
 * @returns {Object} e.g. { 'hectare_equivalent:Tall': 6349.21, 'hectare_equivalent:Gran': 1587.3 }
 */
export function buildPerClassHectareEquivalent(detectionClasses) {
	const result = {};
	for (const cls of detectionClasses || []) {
		if (cls.className) {
			result[`hectare_equivalent:${cls.className}`] = cls.hectare_equivalent || 0;
		}
	}
	return result;
}

/**
 * Computes enrichment metadata for a single DSP plot based on matching detections.
 * @param {Object} params
 * @param {number} plotArea - The plot area in m²
 * @param {number} totalDetectsCount - Total detection count from DSP data
 * @param {Array} detectionClasses - Array of { id, count, hectare_equivalent, className }
 * @param {Array} matchingDetections - Filtered detections matching this plot's filename
 * @returns {Object} metadata object
 */
export function computePlotMetadata({
	plotArea,
	totalDetectsCount,
	detectionClasses,
	matchingDetections,
}) {
	const hectareEquivalent = (detectionClasses || []).reduce(
		(sum, cls) => sum + (cls.hectare_equivalent || 0),
		0
	);

	const perClassHectareEquivalent = buildPerClassHectareEquivalent(detectionClasses);

	if (!matchingDetections || matchingDetections.length === 0) {
		return {
			count: totalDetectsCount ?? 0,
			area: plotArea ?? 0,
			total_area: null,
			mean_height: null,
			confidence: null,
			density: null,
			mean_crown_diameter: null,
			hectare_equivalent: hectareEquivalent,
			...perClassHectareEquivalent, // e.g. 'hectare_equivalent:Tall': 6349
		};
	}

	const heights = matchingDetections
		.map(d => d.properties?.height)
		.filter(v => v != null && !isNaN(v));

	const confidences = matchingDetections
		.map(d => d.properties?.confidence)
		.filter(v => v != null && !isNaN(v));

	const areas = matchingDetections
		.map(d => d.properties?.area)
		.filter(v => v != null && !isNaN(v));

	const diameters = matchingDetections
		.map(d => {
			const r = d.properties?.radius;
			if (r == null || isNaN(r)) return null;
			return r * 2;
		})
		.filter(v => v != null);

	const mean_height =
		heights.length > 0 ? heights.reduce((sum, v) => sum + v, 0) / heights.length : null;

	const confidence =
		confidences.length > 0
			? confidences.reduce((sum, v) => sum + v, 0) / confidences.length
			: null;

	const total_area = areas.length > 0 ? areas.reduce((sum, v) => sum + v, 0) : null;

	const mean_crown_diameter =
		diameters.length > 0 ? diameters.reduce((sum, v) => sum + v, 0) / diameters.length : null;

	// Density uses actual matched detection count from normalized geojson,
	// not the DSP API count (totalDetectsCount), since the normalized data
	// is the source of truth for spatial density calculations.
	// TODO: Verify per-dekar (* 1000) is correct unit — must match DEFAULT_PRESET thresholds in matrixClassification.js
	const density = plotArea > 0 ? (matchingDetections.length / plotArea) * 1000 : null;

	return {
		count: totalDetectsCount ?? 0,
		area: plotArea ?? 0,
		total_area,
		mean_height,
		confidence,
		density,
		mean_crown_diameter,
		hectare_equivalent: hectareEquivalent,
		...perClassHectareEquivalent, // e.g. 'hectare_equivalent:Tall': 6349
	};
}

/**
 * Enriches DSP features with aggregated detection statistics from normalized detections.
 * Pure enrichment logic extracted for testability.
 *
 * NOTE: Mutates features in place by calling feature.set('data', { metadata }).
 * This is consistent with how OpenLayers features are used throughout the codebase.
 *
 * @param {Object} params
 * @param {Object} params.dspData - Raw DSP API response containing plot_data with filenames
 * @param {Array<Feature>} params.dspFeatures - Parsed DSP OL features (from parseDspData)
 * @param {Object|null} params.normalizedDetections - The detections_normalized.geojson response
 * @returns {Array<Feature>} - DSP features with enriched metadata at values_.data.metadata
 */
export function enrichDspFeatures({ dspData, dspFeatures, normalizedDetections }) {
	if (!dspFeatures || dspFeatures.length === 0) return dspFeatures || [];

	if (!normalizedDetections?.features || !dspData?.plot_data) {
		// Set partial metadata from DSP API data already on features.
		// This ensures per-class hectare equivalents are available for
		// classification even when normalized detections haven't loaded.
		// Previously this path returned features untouched (no metadata),
		// causing all plots to classify as "unclassified" until both APIs resolved.
		// Now, properties derived from the DSP API (count, area, hectare_equivalent)
		// are available immediately, enabling faster classification for templates
		// that don't depend on normalized detection properties.
		dspFeatures.forEach(feature => {
			const detectionClasses = feature.get('detectionClasses') || [];
			const totalDetectsCount = feature.get('totalDetectsCount') ?? 0;
			const plotArea = feature.get('area') ?? 0;

			const hectareEquivalent = detectionClasses.reduce(
				(sum, cls) => sum + (cls.hectare_equivalent || 0),
				0
			);

			const perClassHectareEquivalent = buildPerClassHectareEquivalent(detectionClasses);

			feature.set('data', {
				metadata: {
					count: totalDetectsCount,
					area: plotArea,
					hectare_equivalent: hectareEquivalent,
					...perClassHectareEquivalent,
					// Properties requiring normalized detections remain null
					mean_height: null,
					confidence: null,
					density: null,
					total_area: null,
					mean_crown_diameter: null,
				},
			});
		});

		return dspFeatures;
	}

	// Filter plot entries the same way parseDspData does to maintain index alignment.
	// parseDspData uses Object.entries(data.plot_data).filter(([_, item]) => item?.metadata?.plot_polygon)
	// so the ordering here matches the feature array produced by parseDspData.
	const plotEntries = Object.entries(dspData.plot_data).filter(
		([_, item]) => item?.metadata?.plot_polygon
	);

	const detectionsByTileName = {};
	for (const detection of normalizedDetections.features) {
		const tileName = detection?.properties?.tile_name;
		if (tileName) {
			if (!detectionsByTileName[tileName]) {
				detectionsByTileName[tileName] = [];
			}
			detectionsByTileName[tileName].push(detection);
		}
	}

	dspFeatures.forEach((feature, index) => {
		if (index >= plotEntries.length) return;

		const [, plotItem] = plotEntries[index];
		const filename = plotItem.metadata.filename;
		const matchingDetections = detectionsByTileName[filename] || [];

		const plotArea = feature.get('area') ?? 0;
		const totalDetectsCount = feature.get('totalDetectsCount') ?? 0;
		const detectionClasses = feature.get('detectionClasses') || [];

		const metadata = computePlotMetadata({
			plotArea,
			totalDetectsCount,
			detectionClasses,
			matchingDetections,
		});

		feature.set('data', { metadata });
	});

	return dspFeatures;
}

/**
 * React hook that enriches DSP features with aggregated detection statistics.
 * Uses useMemo to avoid recomputation on every render.
 *
 * @param {Object} params
 * @param {Object} params.dspData - Raw DSP API response containing plot_data with filenames
 * @param {Array<Feature>} params.dspFeatures - Parsed DSP OL features (from parseDspData)
 * @param {Object|null} params.normalizedDetections - The detections_normalized.geojson response
 * @returns {Array<Feature>} - DSP features with enriched metadata at values_.data.metadata
 */
const useDspEnrichment = ({ dspData, dspFeatures, normalizedDetections }) => {
	return useMemo(
		() => enrichDspFeatures({ dspData, dspFeatures, normalizedDetections }),
		[dspData, dspFeatures, normalizedDetections]
	);
};

export default useDspEnrichment;
